import { ReplenishmentPlan } from '@ekkogmbh/apisdk';
import { Chip, Divider, Stack, Typography } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { ReactNode, useMemo } from 'react';
import TooltipHardened from 'src/Common/Components/TooltipHardened';

export default function ReplenishmentPlanChip({
  replenishmentPlan,
}: {
  replenishmentPlan: ReplenishmentPlan;
}): React.JSX.Element {
  const tooltipContent: ReactNode = useMemo(() => {
    const machine = replenishmentPlan.linearStateMachineDefinition;
    if (machine === undefined) {
      return undefined;
    }

    return (
      <Stack spacing={1.5} sx={{ p: 1.5, minWidth: 200 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          textTransform="uppercase"
          letterSpacing={1}
          color="primary.main"
          textAlign="center"
        >
          State Machine
        </Typography>
        <Divider />
        <Stack spacing={1}>
          {machine.states.map((state: string, index: number) => (
            <Stack key={index} spacing={0.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {index > 0 && <ArrowForward sx={{ fontSize: 14, color: 'text.secondary' }} />}
                <Typography variant="body2" fontWeight={700}>
                  {state}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ pl: index > 0 ? 3.5 : 0 }}>
                <Typography variant="caption" color="text.secondary">
                  trigger:
                </Typography>
                <Typography variant="caption" fontFamily="monospace">
                  {machine.triggers[state].type}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ pl: index > 0 ? 3.5 : 0 }}>
                <Typography variant="caption" color="text.secondary">
                  action:
                </Typography>
                <Typography variant="caption" fontFamily="monospace">
                  {machine.onEnterActions[state].name}
                </Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
    );
  }, [replenishmentPlan.linearStateMachineDefinition]);

  const chipContent = useMemo(
    () => (
      <Stack>
        <Typography
          color={'textPrimary'}
          style={{ fontSize: 16, lineHeight: 'normal', fontWeight: 700, userSelect: 'none' }}
        >
          {replenishmentPlan.name}
        </Typography>
        <Typography color={'textSecondary'} style={{ fontSize: 12, lineHeight: 'normal', userSelect: 'none' }}>
          {replenishmentPlan.coordinate}
        </Typography>
      </Stack>
    ),
    [replenishmentPlan.name, replenishmentPlan.coordinate],
  );

  return (
    <TooltipHardened arrow tooltip={tooltipContent}>
      <Chip
        variant={'outlined'}
        style={{
          position: 'relative',
          margin: 8,
          height: 48,
          width: 'fit-content',
          overflow: 'hidden',
          flexShrink: 0,
        }}
        label={chipContent}
      />
    </TooltipHardened>
  );
}
